import React, { useState } from 'react';
import { FiPaperclip, FiDownload, FiFolder, FiExternalLink } from 'react-icons/fi';
import api from '../api';
import { useAuth } from '../AuthContext';
import resolveDownloadPath from '../utils/downloads';

export default function FileAttachment({ file, onOpenFileManager }) {
  const { token } = useAuth();
  const [downloading, setDownloading] = useState(false);
  const [opening, setOpening] = useState(false);

  if (!file) {
    return <span className="text-sm text-slate-500">Sem arquivo</span>;
  }

  const meta =
    typeof file === 'string'
      ? { url: file, name: 'Baixar arquivo', metadata: null }
      : {
          url: file.url,
          downloadUrl: file.downloadUrl,
          downloadPath: file.downloadPath,
          name: file.name || 'Baixar arquivo',
          relativePath: file.relativePath,
          metadata: file.metadata || null,
        };

  const resolveFileRequestPath = () => {
    if (meta.downloadUrl) return resolveDownloadPath(meta.downloadUrl);
    if (meta.downloadPath) return resolveDownloadPath(meta.downloadPath);
    return null;
  };

  const buildRequestCandidates = (primaryPath) => {
    const candidates = [];
    const basePathname = (() => {
      try {
        const fallbackOrigin = typeof window !== 'undefined' ? window.location.origin : 'https://localhost';
        const baseUrl = api?.defaults?.baseURL || fallbackOrigin;
        const parsed = new URL(baseUrl, fallbackOrigin);
        const normalizedPath = parsed.pathname.replace(/\/{2,}/g, '/').replace(/\/+$/, '');
        return normalizedPath && normalizedPath !== '/' ? normalizedPath : '';
      } catch {
        return '';
      }
    })();

    const normalizeCandidate = (value) => {
      if (!value || typeof value !== 'string') return '';

      const trimmed = value.trim();
      if (!trimmed || /^https?:\/\//i.test(trimmed)) return '';

      const withLeadingSlash = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
      const [pathname = '', query = ''] = withLeadingSlash.split('?');
      let cleanPathname = pathname
        .replace(/\/{2,}/g, '/')
        .replace(/^\/api(?:\/api)+(\/|$)/i, '/api$1')
        .replace(/^\/(?:api\/)+files(\/|$)/i, '/files$1')
        .replace(/^\/(?:api\/)+uploads(\/|$)/i, '/uploads$1');

      if (basePathname && cleanPathname.toLowerCase().startsWith(`${basePathname.toLowerCase()}/`)) {
        cleanPathname = cleanPathname.slice(basePathname.length);
      }

      if (!cleanPathname.startsWith('/')) {
        cleanPathname = `/${cleanPathname}`;
      }

      if (basePathname && cleanPathname.toLowerCase().startsWith(`${basePathname.toLowerCase()}/`)) {
        cleanPathname = cleanPathname.slice(basePathname.length);
      }

      if (!cleanPathname.startsWith('/')) {
        cleanPathname = `/${cleanPathname}`;
      }

      if (basePathname && cleanPathname.toLowerCase().startsWith(`${basePathname.toLowerCase()}/`)) {
        cleanPathname = cleanPathname.slice(basePathname.length);
      }

      if (!cleanPathname.startsWith('/')) {
        cleanPathname = `/${cleanPathname}`;
      }

      if (basePathname && cleanPathname.toLowerCase().startsWith(`${basePathname.toLowerCase()}/`)) {
        cleanPathname = cleanPathname.slice(basePathname.length);
      }

      if (!cleanPathname.startsWith('/')) {
        cleanPathname = `/${cleanPathname}`;
      }

      if (basePathname && cleanPathname.toLowerCase().startsWith(`${basePathname.toLowerCase()}/`)) {
        cleanPathname = cleanPathname.slice(basePathname.length);
      }

      if (!cleanPathname.startsWith('/')) {
        cleanPathname = `/${cleanPathname}`;
      }

      if (basePathname && cleanPathname.toLowerCase().startsWith(`${basePathname.toLowerCase()}/`)) {
        cleanPathname = cleanPathname.slice(basePathname.length);
      }

      if (!cleanPathname.startsWith('/')) {
        cleanPathname = `/${cleanPathname}`;
      }

      if (basePathname && cleanPathname.toLowerCase().startsWith(`${basePathname.toLowerCase()}/`)) {
        cleanPathname = cleanPathname.slice(basePathname.length);
      }

      if (!cleanPathname.startsWith('/')) {
        cleanPathname = `/${cleanPathname}`;
      }

      if (basePathname && cleanPathname.toLowerCase().startsWith(`${basePathname.toLowerCase()}/`)) {
        cleanPathname = cleanPathname.slice(basePathname.length);
      }

      if (!cleanPathname.startsWith('/')) {
        cleanPathname = `/${cleanPathname}`;
      }

      if (basePathname && cleanPathname.toLowerCase().startsWith(`${basePathname.toLowerCase()}/`)) {
        cleanPathname = cleanPathname.slice(basePathname.length);
      }

      if (!cleanPathname.startsWith('/')) {
        cleanPathname = `/${cleanPathname}`;
      }

      if (basePathname && cleanPathname.toLowerCase().startsWith(`${basePathname.toLowerCase()}/`)) {
        cleanPathname = cleanPathname.slice(basePathname.length);
      }

      if (!cleanPathname.startsWith('/')) {
        cleanPathname = `/${cleanPathname}`;
      }

      return `${cleanPathname}${query ? `?${query}` : ''}`;
    };

    const pushCandidate = (value) => {
      const normalized = normalizeCandidate(value);
      if (!normalized) return;
      if (!candidates.includes(normalized)) {
        candidates.push(normalized);
      }
    };

    const toPathAndQuery = (value) => {
      if (typeof value !== 'string' || !value.trim()) return null;
      const trimmed = value.trim();
      if (/^https?:\/\//i.test(trimmed)) return null;

      const withLeadingSlash = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
      const [pathname = '', query = ''] = withLeadingSlash.split('?');
      return { pathname, query: query ? `?${query}` : '' };
    };

    const addFileEndpointVariants = (value) => {
      const parsed = toPathAndQuery(value);
      if (!parsed) return;

      const normalizedPath = parsed.pathname.replace(/^\/(?:api\/)+files$/i, '/files');
      if (/^\/files$/i.test(normalizedPath)) {
        pushCandidate(`/files${parsed.query}`);
        return;
      }

      if (/^\/api\/files$/i.test(parsed.pathname)) {
        pushCandidate(`/files${parsed.query}`);
        return;
      }

      pushCandidate(`${parsed.pathname}${parsed.query}`);
    };

    const addUploadsVariants = (value) => {
      const parsed = toPathAndQuery(value);
      if (!parsed || !parsed.query) return;
      const params = new URLSearchParams(parsed.query.slice(1));
      const rawPath = params.get('path');
      if (!rawPath) return;
      const decodedPath = rawPath.trim().replace(/^\/+/, '');
      if (!decodedPath) return;
      pushCandidate(`/uploads/${decodedPath}`);
    };

    const sources = [
      primaryPath,
      resolveDownloadPath(meta.downloadUrl || ''),
      resolveDownloadPath(meta.downloadPath || ''),
    ];

    sources.forEach((source) => {
      addFileEndpointVariants(source);
      addUploadsVariants(source);
    });

    return candidates;
  };

  const fetchFileBlob = async (primaryPath) => {
    const candidates = buildRequestCandidates(primaryPath);
    const baseURL = String(api?.defaults?.baseURL || '').trim();
    const baseHasApiPrefix = (() => {
      if (!baseURL) return false;
      try {
        const parsed = new URL(baseURL, window.location.origin);
        return /\/api\/?$/i.test(parsed.pathname);
      } catch (error) {
        return /\/api\/?$/i.test(baseURL);
      }
    })();

    const normalizeForAxios = (candidate) => {
      if (!candidate || typeof candidate !== 'string') return candidate;
      if (/^https?:\/\//i.test(candidate)) return candidate;

      if (baseHasApiPrefix && /^\/api(\/|$)/i.test(candidate)) {
        const stripped = candidate.replace(/^\/api(?=\/|$)/i, '');
        return stripped || '/';
      }

      return candidate;
    };

    let lastError = null;

    for (const candidate of candidates) {
      try {
        const response = await api.get(normalizeForAxios(candidate), { responseType: 'blob' });
        return response;
      } catch (error) {
        lastError = error;
        const status = error?.response?.status;
        if (status && status !== 404) {
          throw error;
        }
      }
    }

    throw lastError || new Error('Arquivo não encontrado');
  };

  const handleOpen = async (event) => {
    event.preventDefault();
    if (opening) return;

    const requestPath = resolveFileRequestPath();
    if (!requestPath) {
      if (meta.url) {
        window.open(meta.url, '_blank', 'noopener');
      }
      return;
    }

    if (!token) {
      alert('Faça login para abrir este arquivo.');
      return;
    }

    setOpening(true);
    try {
      const response = await fetchFileBlob(requestPath);
      const blobUrl = window.URL.createObjectURL(response.data);
      window.open(blobUrl, '_blank', 'noopener,noreferrer');
      setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl);
      }, 60_000);
    } catch (err) {
      console.error('Erro ao abrir arquivo', err);
      alert('Não foi possível abrir este arquivo.');
    } finally {
      setOpening(false);
    }
  };

  const handleDownload = async (event) => {
    event.preventDefault();
    if (downloading) return;

    const requestPath = resolveFileRequestPath();
    if (!requestPath) {
      if (meta.url) {
        window.open(meta.url, '_blank', 'noopener');
      }
      return;
    }

    if (!token) {
      alert('Faça login para baixar este arquivo.');
      return;
    }

    setDownloading(true);
    try {
      const response = await fetchFileBlob(requestPath);
      const blobUrl = window.URL.createObjectURL(response.data);
      const anchor = document.createElement('a');
      anchor.href = blobUrl;
      anchor.download = meta.name || 'arquivo';
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('Erro ao baixar arquivo', err);
      alert('Não foi possível baixar este arquivo.');
    } finally {
      setDownloading(false);
    }
  };

  const showFileManagerButton = typeof onOpenFileManager === 'function';
  const relativeHint = meta.relativePath;
  const requestHint = (() => {
    if (!meta.metadata) return '';
    const parts = [];
    if (meta.metadata.requestId) {
      parts.push(`Solicitação ${meta.metadata.requestId}`);
    }
    if (meta.metadata.requestProtocol) {
      parts.push(`Protocolo ${meta.metadata.requestProtocol}`);
    }
    if (meta.metadata.requesterName) {
      parts.push(meta.metadata.requesterName);
    }
    if (meta.metadata.requesterSector) {
      parts.push(meta.metadata.requesterSector);
    }
    if (meta.metadata.insertedAt) {
      try {
        const when = new Date(meta.metadata.insertedAt);
        if (!Number.isNaN(when.getTime())) {
          parts.push(when.toLocaleString());
        }
      } catch {}
    }
    return parts.join(' · ');
  })();

  return (
    <div className="flex w-full items-center justify-between rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm shadow-sm">
      <div className="flex min-w-0 flex-1 items-center space-x-2">
        <FiPaperclip className="text-slate-500" />
        <div className="flex min-w-0 flex-col">
          <span className="truncate font-medium text-slate-700" title={meta.name || 'Arquivo'}>
            {meta.name || 'Arquivo'}
          </span>
          {relativeHint && (
            <span className="truncate text-xs text-slate-400" title={relativeHint}>
              {relativeHint}
            </span>
          )}
          {requestHint && (
            <span className="truncate text-xs text-slate-400" title={requestHint}>
              {requestHint}
            </span>
          )}
        </div>
      </div>
      <div className="ml-3 flex items-center gap-2">
        <button
          type="button"
          onClick={handleOpen}
          disabled={opening}
          className={`inline-flex items-center space-x-1 rounded-md px-3 py-1 text-xs font-semibold transition focus:outline-none focus:ring-2 focus:ring-blue-200 ${
            opening
              ? 'cursor-not-allowed border border-blue-300 bg-blue-200 text-white'
              : 'border border-blue-500 bg-blue-500 text-white hover:bg-blue-600'
          }`}
        >
          <FiExternalLink />
          <span>{opening ? 'Abrindo...' : 'Abrir'}</span>
        </button>

        <button
          type="button"
          onClick={handleDownload}
          disabled={downloading}
          className={`inline-flex items-center space-x-1 rounded-md px-3 py-1 text-xs font-semibold transition focus:outline-none focus:ring-2 focus:ring-blue-200 ${
            downloading
              ? 'cursor-not-allowed border border-blue-300 bg-blue-200 text-white'
              : 'border border-blue-500 bg-blue-500 text-white hover:bg-blue-600'
          }`}
        >
          <FiDownload />
          <span>{downloading ? 'Baixando...' : 'Baixar'}</span>
        </button>

        {showFileManagerButton && (
          <button
            type="button"
            onClick={() => onOpenFileManager(file)}
            className="inline-flex items-center space-x-1 rounded-md border border-blue-500 bg-blue-500 px-3 py-1 text-xs font-semibold text-white transition hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-200"
          >
            <FiFolder />
            <span>Gerenciador</span>
          </button>
        )}
      </div>
    </div>
  );
}
